import {
  Button,
  DatePicker,
  FormField,
  Input,
  Multiselect,
  Select,
  SelectProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import useUserProfile from 'src/hooks/useUserProfile';
import { useEffect } from 'react';
import { useDivision } from 'src/store/division/useDivision';
import { Division } from 'src/types/orbit/Division';
import { SearchAuditsRequest } from 'src/types/helios/Audit';
import SingleSelectDivision from '../division/SingleSelectDivision';
import MultiSelectTemplate from '../template/MultiSelectTemplate';
import EmployeePicker from '../common/EmployeePicker';
import { useTemplates } from 'src/store/templates/useTemplates';
import AuditTypeSelect from './AuditTypeSelect';
import MultiSelectAuditStatus from './MultiSelectAuditStatus';
import { AuditStatus } from 'src/contstant/commonLabels';
import { convertList } from 'src/utils/generalUtils';
import { useLocations } from 'src/store/locations/useLocations';
import { Location } from 'src/types/helios/Location';

interface AuditFilterViewProps {
  defaultSearchParams: SearchAuditsRequest; // Current filter state (required)
  resetSearchParams: SearchAuditsRequest; // What to reset to (required)
  onSearchChange: (searchParams: SearchAuditsRequest) => void;
  onClearAll?: () => void;
}
const AuditFilterView = ({
  onSearchChange,
  defaultSearchParams,
  resetSearchParams,
  onClearAll,
}: AuditFilterViewProps) => {
  const { getUserBusinessUnitId, getUserProgramId, currentUser, getLocations } =
    useUserProfile();
  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();

  const { locationsList, loadLocations } = useLocations();
  const { templatesList, loadTemplatesList } = useTemplates();

  // Default audit statuses - excludes CANCELLED to avoid cluttering the view
  const DEFAULT_AUDIT_STATUSES = [
    'NOT_STARTED',
    'SCHEDULED',
    'IN_PROGRESS',
    'COMPLETED',
    'EXPIRED',
  ];

  const dateRangeList = [
    { label: 'Past Week', value: 'PAST_WEEK' },
    { label: 'Past Month', value: 'PAST_MONTH' },
    { label: 'Past Year', value: 'PAST_YEAR' },
    { label: 'Custom', value: 'CUSTOM' },
  ];

  // Use defaultSearchParams directly as the source of truth (controlled component)
  const searchParams = defaultSearchParams;

  useEffect(() => {
    // Load data based on the businessUnitId in searchParams
    // This could be from shared URL or user's current BU
    // The parent component (AuditsPage) handles BU mismatch validation
    if (searchParams.businessUnitId) {
      loadDivisionsByBusinessUnit({
        businessUnitId: searchParams.businessUnitId,
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
      loadLocations({
        businessUnitId: searchParams.businessUnitId,
      });
      loadTemplatesList({
        program: getUserProgramId(),
        businessUnitId: searchParams.businessUnitId,
      });
    }
  }, [currentUser, searchParams.businessUnitId]);

  return (
    <SpaceBetween size="l">
      <FormField label="Location" data-testid="location-field">
        <Multiselect
          enableSelectAll
          filteringType="auto"
          data-testid="location-dropdown"
          options={convertList<Location, SelectProps.Option>(locationsList, (item) => ({
            value: item.locationCode,
            label: item.locationCode,
          }))}
          selectedOptions={
            searchParams?.locations?.map((loc) => ({
              label: loc,
              value: loc,
            })) || []
          }
          inlineTokens
          onChange={({ detail }) => {
            const locations = detail.selectedOptions.map((opt) => opt.value as string);
            const newParams = {
              ...searchParams,
              locations: locations.length > 0 ? locations : undefined,
            };
            onSearchChange(newParams);
          }}
          loadingText="Loading Location"
          placeholder="Choose Location"
        />
      </FormField>

      <FormField label="Division" data-testid="division-field">
        <SingleSelectDivision
          data-testid="division-select"
          divisions={divisionList}
          isLoading={fetcDivisionsApiStatus.isLoading}
          error={fetcDivisionsApiStatus.errorMessage}
          selectedDivision={searchParams.divisionName || null}
          onItemSelection={(item: Division | null) => {
            const divisionName = item?.divisionName || '';
            const newParams = {
              ...searchParams,
              divisionName,
            };
            onSearchChange(newParams);
          }}
        />
      </FormField>

      <FormField data-testid="template-id-field" label="Template">
        <MultiSelectTemplate
          templates={templatesList}
          selectedTemplateIds={searchParams.templateIds || []}
          onSelectionChange={(selectedIds) => {
            const newParams = {
              ...searchParams,
              templateIds: selectedIds,
            };
            onSearchChange(newParams);
          }}
          data-testid="templateId-multiselect"
          placeholder="Select templates"
        />
      </FormField>

      <FormField data-testid="audit-type-field" label="Audit Type">
        <AuditTypeSelect
          data-testid="audit-type-select"
          selectedAuditType={searchParams.auditTypes?.[0]}
          onSelectionChange={(auditType: string) => {
            const newParams = {
              ...searchParams,
              auditTypes: [auditType],
            };
            onSearchChange(newParams);
          }}
        />
      </FormField>

      <FormField data-testid="audit-status-field" label="Audit Status">
        <MultiSelectAuditStatus
          selectedStatuses={searchParams.statuses || []}
          onSelectionChange={(statuses) => {
            const newParams = {
              ...searchParams,
              statuses: statuses,
            };
            onSearchChange(newParams);
          }}
          data-testid="audit-status-multiselect"
          placeholder="Select status"
        />
      </FormField>

      <FormField data-testid="assigned-to-field" label="Assigned To">
        <EmployeePicker
          programId={getUserProgramId()}
          value={searchParams.assignees?.[0] || ''}
          data-testid="assigned-to-input"
          onSelect={(login) => onSearchChange({ ...searchParams, assignees: [login] })}
          onClear={() => onSearchChange({ ...searchParams, assignees: undefined })}
          placeholder="Search by alias or name"
        />
      </FormField>

      <FormField label="Date Range" data-testid="date-range-field">
        <Select
          data-testid="date-range-select"
          selectedOption={
            searchParams.dateRange?.type
              ? dateRangeList.find((item) => item.value === searchParams.dateRange?.type)!
              : null
          }
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              dateRange: {
                type: detail.selectedOption.value as string,
              },
            };
            onSearchChange(newParams);
          }}
          options={dateRangeList}
          placeholder="Select date range"
        />
      </FormField>

      {searchParams?.dateRange?.type === 'CUSTOM' && (
        <>
          <FormField label="Start Date" stretch data-testid="start-date-field">
            <DatePicker
              data-testid="start-date-picker"
              onChange={({ detail }) => {
                const newParams = {
                  ...searchParams,
                  dateRange: {
                    ...searchParams.dateRange,
                    type: 'CUSTOM',
                    startDate: detail.value,
                  },
                };
                onSearchChange(newParams);
              }}
              placeholder="YYYY/MM/DD"
              value={searchParams.dateRange?.startDate || ''}
            />
          </FormField>

          <FormField label="End Date" stretch data-testid="end-date-field">
            <DatePicker
              data-testid="end-date-picker"
              onChange={({ detail }) => {
                const newParams = {
                  ...searchParams,
                  dateRange: {
                    ...searchParams.dateRange,
                    type: 'CUSTOM',
                    endDate: detail.value,
                  },
                };
                onSearchChange(newParams);
              }}
              placeholder="YYYY/MM/DD"
              value={searchParams.dateRange?.endDate || ''}
            />
          </FormField>
        </>
      )}
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="reset-button"
          onClick={() => {
            // Reset to resetSearchParams (provided by parent - either shared or default filters)
            onSearchChange(resetSearchParams);
          }}
        >
          Reset
        </Button>
        <Button
          data-testid="clear-all-button"
          onClick={() => {
            if (onClearAll) {
              onClearAll();
            }
          }}
        >
          Clear All
        </Button>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default AuditFilterView;
